<template>
  <div
    :class="[
      'styled-container',
      `sc-variant-${variant}`,
      {
        'is-pressable': props.fields?.pressable,
        'has-backdrop-blur': backdropBlur,
        'has-fill-opacity': fillOpacity,
        [MAX_WIDTH_ALIGNED_CLASS]: isMaxWidthAligned(rootStyle),
      },
    ]"
    :style="rootStyle"
    :role="isInteractive() ? 'button' : undefined"
    :tabindex="isInteractive() ? 0 : undefined"
    @click="onActivate"
    @keydown="onKeyDown"
  >
    <img
      v-if="imageProps && imageShouldRender"
      v-bind="imageProps.imgAttrs"
      alt=""
      class="sc-bg-image"
      :class="{ 'is-hidden': imageShouldFadeOut }"
      :style="mediaStyle"
    />
    <video
      v-if="videoUrl"
      ref="videoEl"
      :src="videoUrl"
      :autoplay="props.fields?.videoAutoplay !== false"
      :loop="props.fields?.videoLoop !== false"
      :muted="props.fields?.videoMuted !== false"
      playsinline
      class="sc-bg-video"
      :style="mediaStyle"
      @canplay="videoReady = true"
    />
    <div v-if="variant === 'offer-card-featured'" class="sc-ribbon">
      <TipTap v-if="props.fields?.ribbonText?.key" v-model="ribbonTextValue" mode="single" />
    </div>
    <div class="sc-content" :style="contentStyle">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, type CSSProperties } from 'vue'
import {
  composeShadow,
  fadeColor,
  getSpacingStyleData,
  getMaxWidthMarginStyleData,
  getColorStyleData,
  getGradientStyleData,
  getOpacityPercent,
  getRadiusStyleData,
  getSizeStyleData,
} from '../../../composables/useStyleData'
import { MAX_WIDTH_ALIGNED_CLASS, isMaxWidthAligned } from '../../../lib/maxWidthAlign'
import { useImagePreset } from '../../../composables/useImagePreset'
import { usePipe } from '@render/composables/usePipe'
import { useClickAction } from '@render/composables/useClickAction'
import TipTap from '@render/lib/TipTap'
import { useRuntimeConfig } from '#imports'
import type { StyledContainerFields } from './definition'

const props = withDefaults(
  defineProps<{
    fields?: StyledContainerFields
  }>(),
  {
    fields: () => ({}),
  },
)

const emit = defineEmits(['onClick'])

// `pressable` is the STYLING flag (cursor, hover/active/focus treatment). It is
// deliberately not the only thing that makes the container a control: an author
// who wires an onClick processor without ticking it used to get a silent no-op
// — a click that fired nothing and a div that announced nothing. A bound click
// action now activates and is announced too; the `is-pressable` visuals stay
// opt-in so existing pipes render byte-identically.
const { hasClickAction } = useClickAction()
const isInteractive = () => !!props.fields?.pressable || hasClickAction()

const pipe = usePipe()

const variant = computed(() => props.fields?.styleVariant || 'list-item')

const ribbonTextValue = computed({
  get: () => pipe.getTranslation(props.fields?.ribbonText?.key ?? ''),
  set: (val: object) => pipe.setTranslation(props.fields?.ribbonText?.key ?? '', val),
})

const videoReady = ref(false)
const videoEl = ref<HTMLVideoElement>()

const runtimeConfig = useRuntimeConfig()

const videoUrl = computed(() => {
  const id = props.fields?.backgroundVideo?.id
  if (!id) return ''
  const bundledVideos = (runtimeConfig.public as { bundledVideos?: Record<string, string> })
    ?.bundledVideos
  const bundledFile = bundledVideos?.[id]
  if (bundledFile) {
    const base = runtimeConfig.app?.baseURL ?? '/'
    return `${base}videos/${bundledFile}`.replace(/([^:])\/{2,}/g, '$1/')
  }
  return `${import.meta.env.VITE_MEDIA_HOST || ''}/api/media/${id}`
})

watch(
  () => props.fields?.backgroundVideo?.id,
  () => {
    videoReady.value = false
  },
)

const imageProps = useImagePreset(() => props.fields?.backgroundImage)

const hasVideo = computed(() => !!videoUrl.value)
const hasImage = computed(() => !!imageProps.value)

const imageShouldRender = computed(() => {
  if (!hasImage.value) return false
  if (!hasVideo.value) return true
  if (props.fields?.showImageUntilVideoLoads === false) return false
  return true
})

const imageShouldFadeOut = computed(
  () => hasVideo.value && videoReady.value && props.fields?.showImageUntilVideoLoads !== false,
)

const backdropBlur = computed(() => {
  const px = props.fields?.backdropBlur
  return px && px > 0 ? `${px}px` : ''
})

const fillOpacity = computed(() => getOpacityPercent(props.fields?.bgOpacity))

const mediaStyle = computed<CSSProperties>(() => ({
  objectFit: (props.fields?.imageObjectFit || 'cover') as CSSProperties['objectFit'],
  objectPosition: props.fields?.imageObjectPosition || 'center',
}))

const contentStyle = computed(() => {
  const f = props.fields
  const s: Record<string, string> = {}
  const layout = f?.layout || 'block'

  if (layout !== 'block') {
    s.display = 'flex'
    s.flexDirection = layout
    const gap = getSpacingStyleData(f?.gap)
    if (gap) s.gap = gap
    if (f?.alignItems) s.alignItems = f.alignItems
    if (f?.justifyContent) s.justifyContent = f.justifyContent
    if (f?.wrap) s.flexWrap = 'wrap'
  }

  return s
})

const rootStyle = computed(() => {
  const f = props.fields
  const s: Record<string, string> = {}

  const width = getSizeStyleData(f?.width)
  if (width) s.width = width
  const minWidth = getSizeStyleData(f?.minWidth)
  if (minWidth) s.minWidth = minWidth
  const maxWidth = getSizeStyleData(f?.maxWidth)
  if (maxWidth) s.maxWidth = maxWidth
  const height = getSizeStyleData(f?.height)
  if (height) s.height = height
  const minHeight = getSizeStyleData(f?.minHeight)
  if (minHeight) s.minHeight = minHeight
  const maxHeight = getSizeStyleData(f?.maxHeight)
  if (maxHeight) s.maxHeight = maxHeight
  s.overflowX = f?.overflowX || 'hidden'
  s.overflowY = f?.overflowY || 'hidden'
  const padding = getSpacingStyleData(f?.padding)
  if (padding) s.padding = padding
  // The authored margin, and a capped box placed by its alignment. Unset keeps the
  // authored margin alone, as every StyledContainer with a max width rendered before the
  // alignment existed.
  Object.assign(s, getMaxWidthMarginStyleData(f?.margin, maxWidth, f?.maxWidthAlign, undefined))
  const bgColor = fadeColor(getColorStyleData(f?.bgColor), fillOpacity.value)
  const bgGradient = getGradientStyleData(f?.bgGradient, undefined, fillOpacity.value)
  const borderWidth = getSizeStyleData(f?.borderWidth)
  const borderColor = getColorStyleData(f?.borderColor)
  const borderGradient = getGradientStyleData(f?.borderGradient)

  if (borderGradient) {
    // Gradient border via the double-background technique (same as
    // DiscountCode.vue): fill layers are clipped to the padding box, the
    // border gradient to the border box, and the border itself is transparent
    // so the gradient shows through it. Composes cleanly with a gradient/solid
    // fill and follows borderRadius — unlike border-image, which squares the
    // corners, and unlike a masked ring, which seams against the fill.
    const layers: string[] = []
    const clips: string[] = []
    if (bgGradient) {
      layers.push(bgGradient)
      clips.push('padding-box')
    }
    if (bgColor) {
      layers.push(`linear-gradient(${bgColor}, ${bgColor})`)
      clips.push('padding-box')
    }
    layers.push(borderGradient)
    clips.push('border-box')
    s.backgroundImage = layers.join(', ')
    s.backgroundClip = clips.join(', ')
    s.backgroundOrigin = 'border-box'
    s.borderStyle = 'solid'
    s.borderColor = 'transparent'
    s.borderWidth = borderWidth || 'var(--border-width, 1px)'
  } else {
    if (bgColor) s.backgroundColor = bgColor
    if (bgGradient) s.backgroundImage = bgGradient
    if (borderWidth) s.borderWidth = borderWidth
    if (borderColor) s.borderColor = borderColor
    if (borderWidth || borderColor) s.borderStyle = 'solid'
  }

  const borderRadius = getRadiusStyleData(f?.borderRadius)
  if (borderRadius) s.borderRadius = borderRadius
  const composedShadow = composeShadow(f?.dropShadow, f?.innerShadow)
  if (composedShadow !== 'none') s.boxShadow = composedShadow
  if (backdropBlur.value) s['--sc-backdrop-blur'] = backdropBlur.value
  if (fillOpacity.value) s['--sc-fill-opacity'] = fillOpacity.value

  return s
})

function onActivate(event: MouseEvent) {
  if (!isInteractive()) return
  emit('onClick', event)
}

// Only treat space/enter as "activate" when the container itself has focus.
// If focus is on a descendant (e.g. a TipTap editor inside the ribbon or a
// slotted child), the keystroke belongs to that descendant — swallowing it
// here would break typing (spaces, newlines) inside nested contenteditables.
function onKeyDown(event: KeyboardEvent) {
  if (!isInteractive()) return
  if (event.target !== event.currentTarget) return
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  emit('onClick', event)
}
</script>

<style scoped lang="scss">
.styled-container {
  --sc-fill: transparent;
  position: relative;
  box-sizing: border-box;
  background-color: var(--sc-fill);
}

.sc-bg-image,
.sc-bg-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}

.sc-bg-image {
  opacity: 1;
  transition: opacity 0.4s ease-in;

  &.is-hidden {
    opacity: 0;
  }
}

.sc-content {
  position: relative;
  z-index: 1;
}

.has-backdrop-blur {
  -webkit-backdrop-filter: blur(var(--sc-backdrop-blur));
  backdrop-filter: blur(var(--sc-backdrop-blur));
}

.has-fill-opacity {
  background-color: color-mix(in srgb, var(--sc-fill) var(--sc-fill-opacity), transparent);
}

.is-pressable {
  cursor: pointer;
  user-select: none;

  &:focus-visible {
    outline: 2px solid var(--color-action-primary-border, #3b82f6);
    outline-offset: 2px;
  }
}

// Variant: List Item — matches SelectList item styling
.sc-variant-list-item {
  padding: var(--space-16);
  border-width: var(--border-width);
  border-style: solid;
  border-color: var(--color-action-tertiary-border);
  border-radius: var(--radius-md);
  background-color: var(--color-action-tertiary-background);
  color: var(--color-action-tertiary-text);
  font-family: var(--text-label-md-family);
  font-size: var(--text-label-md-size);
  line-height: var(--text-label-md-line-height);
  font-weight: var(--text-label-md-weight);
  transition: all 0.2s ease;

  &.is-pressable:hover,
  &.is-pressable:focus-visible {
    outline: none;
    border-color: var(--color-action-tertiary-border-pressed);
    box-shadow: var(--shadow-selectors-default);
    transform: translateY(-2px);
  }

  &.is-pressable:active {
    border-color: var(--color-action-tertiary-border-pressed);
    background-color: var(--color-action-tertiary-background-pressed);
    box-shadow: var(--shadow-selectors-pressed);
  }
}

// Variant: Offer Card — matches OfferSelectComponent idle option
.sc-variant-offer-card {
  padding: var(--space-16) var(--space-12);
  border-width: 1px;
  border-style: solid;
  border-color: var(--color-input-border);
  border-radius: var(--radius-xs);
  background-color: var(--color-background-surface-raised);
  transition: all 0.2s ease;

  &.is-pressable:hover,
  &.is-pressable:focus-visible {
    outline: none;
    border-color: var(--color-input-border-active);
  }

  &.is-pressable:active {
    border-color: var(--color-input-border-active);
    box-shadow: var(--shadow-selectors-default-pressed);
  }
}

// Variant: Offer Card (Featured) — offer-card base + best-deal ribbon on top
.sc-variant-offer-card-featured {
  padding: calc(26px + var(--space-16)) var(--space-12) var(--space-16);
  border-width: 1px;
  border-style: solid;
  border-color: var(--color-input-border);
  border-radius: var(--radius-xs);
  background-color: var(--color-background-surface-raised);
  transition: all 0.2s ease;

  .sc-ribbon {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 26px;
    padding: 2px 16px;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    background: var(--color-brand-primary-subtle);
    color: var(--color-action-secondary-text);
    font-size: var(--text-body-sm);
    font-weight: 900;
    line-height: 22px;
    text-transform: uppercase;
    transition:
      background-color 0.2s ease,
      color 0.2s ease;
  }

  &.is-pressable:hover,
  &.is-pressable:focus-visible {
    outline: none;
    border-color: var(--color-input-border-active);

    .sc-ribbon {
      background: var(--color-brand-primary-subtle);
      color: var(--color-action-secondary-text);
    }
  }

  &.is-pressable:active {
    border-color: var(--color-input-border-active);
    box-shadow: var(--shadow-selectors-default-pressed);
  }
}

// Variant: Skeleton — an animated shimmer placeholder for loading states.
// General-purpose (not checkout-specific): size it with the width/height
// fields and stack several to build a skeleton layout; a common use is inside
// a checkout component's "loading" slot. The shimmer is a `::before` overlay so
// it composes with an author-set bgColor and is clipped by borderRadius
// (the container defaults to overflow:hidden).
.sc-variant-skeleton {
  min-height: var(--space-24, 24px);
  // Match the List Item variant's corner rounding by default (author's
  // borderRadius field still overrides via inline style).
  border-radius: var(--radius-md);
  background-color: var(--color-background-surface-sunken, #e5e7eb);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 0;
    // Inherit so the shimmer stays rounded even when overflow isn't clipping.
    border-radius: inherit;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.65) 50%,
      transparent 100%
    );
    background-size: 200% 100%;
    animation: sc-skeleton-shimmer 1.4s ease-in-out infinite;
  }
}

@keyframes sc-skeleton-shimmer {
  0% {
    background-position: 150% 0;
  }
  100% {
    background-position: -150% 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sc-variant-skeleton::before {
    animation: none;
  }
}

// Variant: CTA Button — matches CTAButton styling
.sc-variant-cta-button {
  padding: var(--space-16) var(--space-24);
  border-width: var(--border-width);
  border-style: solid;
  border-color: var(--color-action-primary-border);
  border-radius: var(--radius-md);
  background-color: var(--color-action-primary-background);
  color: var(--color-action-primary-text);
  font-family: var(--text-button-family);
  font-size: var(--text-button-size);
  font-weight: var(--text-button-weight);
  line-height: var(--text-button-line-height);
  text-align: center;
  box-shadow: var(--shadow-cta-button-default), var(--shadow-inner-glow-cta-button-default);
  transition:
    color 150ms cubic-bezier(0.4, 0, 0.2, 1),
    background-color 150ms cubic-bezier(0.4, 0, 0.2, 1),
    border-color 150ms cubic-bezier(0.4, 0, 0.2, 1),
    opacity 150ms cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 150ms cubic-bezier(0.4, 0, 0.2, 1),
    transform 150ms cubic-bezier(0.4, 0, 0.2, 1),
    filter 150ms cubic-bezier(0.4, 0, 0.2, 1);

  &.is-pressable:hover,
  &.is-pressable:focus-visible {
    outline: none;
    background-color: var(--color-action-primary-background-hover);
    box-shadow: var(--shadow-cta-button-hover), var(--shadow-inner-glow-cta-button-hover);
  }

  &.is-pressable:active {
    background-color: var(--color-action-primary-background-pressed);
    border-color: var(--color-action-primary-border-pressed);
    box-shadow: var(--shadow-cta-button-pressed), var(--shadow-inner-glow-cta-button-pressed);
  }
}
</style>
